<template lang="pug">
.subscription-gift-detail(v-if="!loading", v-loading="saving")

  el-header
    el-col(:span="16")
      el-page-header(@back="back", :content="isNew ? 'Novo cupom de presente' : `${coupon.giftDescription}`")

    el-col(:span="8", style="text-align: right")
      el-radio-group(:value="coupon.active ? 'ACTIVE' : 'INACTIVE'", @input="($event) => coupon.active = $event == 'ACTIVE'")
        el-radio-button(key="ACTIVE", label="ACTIVE") Ativo
        el-radio-button(key="INACTIVE", label="INACTIVE") Inativo

      div(style="display: inline-block; width: 10px")

      el-dropdown(split-button, @click="save(false)", @command="command", type="primary", :loading="saving") Salvar
        el-dropdown-menu(slot="dropdown")
          el-dropdown-item(command="saveAndNew") Salvar e criar outro
          el-dropdown-item(command="updateAvailableRedeems" :disabled="isNew") Atualizar resgates disponíveis

  el-tabs
    el-tab-pane(label="Detalhes")
      el-form(ref="form", :model="coupon", :rules="rules" label-width="220px")

        <!-- Coupon codes -->
        el-form-item(label="Cupons" prop="couponCodes")
          el-input(@keyup.enter.native="addCouponCode" v-model="couponInput")
            el-button(slot="append" icon="el-icon-plus" @click="addCouponCode")

          .coupons-container(v-if="coupon.couponCodes && coupon.couponCodes.length > 0")
            el-tag(
            v-for="(couponCode, index) in coupon.couponCodes"
            class="spaced"
            type="primary"
            :key="couponCode"
            :disable-transitions="true"
            )
              | {{ couponCode }}
              i.el-tag__close.el-icon-close(@click.stop="removeCouponCode(index)")

          .coupons-container(v-else)
            .no-coupons Nenhum cupom adicionado

        <!-- Planos -->
        el-form-item(label="Planos" prop="supportedPlans")
          el-checkbox-group(v-model="coupon.supportedPlans")
            el-checkbox-button(label="ANNUALLY") Anual
            el-checkbox-button(label="BIANNUALLY") Semestral
            el-checkbox-button(label="QUARTERLY") Trimestral
            el-checkbox-button(label="MONTHLY") Mensal

        <!-- Produtos presenteados -->
        el-form-item(label="Produtos presenteados" prop="giftProducts")
          RemoteSearchMultiSelect(
            placeholder="Adicionar produto"
            v-model="coupon.giftProducts"
            :search-function="ProductService.search"
            :find-function="ProductService.getProductById"
            :label-function="(item) => `${item.displayName} (#${item.id})`"
            @itemsChanged="setCost"
          )

        <!-- Descrição do presente -->
        el-form-item(label="Descrição do presente", prop="giftDescription")
          el-input(v-model="coupon.giftDescription" type="textarea")

          div(style="line-height: 2; font-size: 11px; color: #999;") ATENÇÃO: Esta descrição é exibida para o usuário no checkout

        el-row
          el-col(:span="12")
            <!-- Custo dos presentes -->
            el-form-item(label="Custo dos presentes (R$)", prop="giftCost")
              el-input-number.stretched(v-model="coupon.giftCost" :controls="false" :min="0" :precision="2")


          el-col(:span="12")
            <!-- Idade mínima -->
            el-form-item(label="Idade mín. para resgatar", prop="minimumAgeToRedeem" label-width="240px")
              el-input-number.stretched(v-model="coupon.minimumAgeToRedeem" :controls="false" :min="0" :max="120" :precision="0")

        el-row
          el-col(:span="12")
            <!-- Quantidade disponível -->
            el-form-item(label="Qtd. de resgates disponíveis", prop="availableRedeems")
              el-input-number.stretched(v-model="coupon.availableRedeems" :controls="false" :min="0" :precision="0" :disabled="!isNew")

          el-col(:span="12")
            <!-- Alertar quando qtd. menor que -->
            el-form-item(label="Alertar quando qtd. menor que", prop="lowAvailableRedeemsThreshold" label-width="240px")
              el-input-number.stretched(v-model="coupon.lowAvailableRedeemsThreshold" :controls="false" :min="0" :precision="0")

    el-tab-pane(label="Estatísticas" lazy)
      StatsTab(
        v-if="coupon.id"
        :id="coupon.id"
      )

  UpdateAvailableRedeems(
    v-if="coupon.id",
    :visible.sync="showUpdateAvailableRedeems",
    :couponId="coupon.id",
    :currentlyAvailableRedeems="coupon.availableRedeems",
    @updated="loadCoupon"
  )
</template>
<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue2-helpers/vue-router";
import { useMessage } from "@/utils/Message";

import UpdateAvailableRedeems from "./actions/UpdateAvailableRedeems.vue";
import StatsTab from "./tabs/StatsTab.vue";
import RemoteSearchMultiSelect from "@/components//input/RemoteSearchMultiSelect.vue";

import SubscriptionGiftCouponService from "@/services/promotions/SubscriptionGiftCouponService";
import ProductService from "@/services/stock/ProductService";

import { onUnmounted } from "vue";
import { nextTick } from "vue";

const route = useRoute();
const router = useRouter();
const message = useMessage();
const coupon = ref({});
const couponInput = ref("");
const dirty = ref(false);
const loading = ref(true);
const saving = ref(false);
const couponId = computed(() => route.params["id"]);
const isNew = computed(() => !couponId.value);
const statsUrl = computed(() => `https://metabase.uptack.io/`)
const form = ref();
const showUpdateAvailableRedeems = ref(false);

const rules = {
  giftDescription: [
    { required: true, trigger: "blur", message: "Descrição obrigatória" },
  ],
  couponCodes: [
    { required: true, trigger: "blur", message: "Cupons obrigatórios" },
  ],
  giftCost: [
    { required: true, trigger: "blur", message: "Custo obrigatório" },
  ],
  giftProducts: [
    { required: true, trigger: "blur", message: "Produtos obrigatórios" },
  ],
  availableRedeems: [
    { required: true, trigger: "blur", message: "Quantidade obrigatória" },
  ],
};

const addCouponCode = () => {
  if (!couponInput.value) return;

  if (!coupon.value.couponCodes) coupon.value.couponCodes = [];

  coupon.value.couponCodes.push(couponInput.value.toUpperCase());
  couponInput.value = "";
};

const removeCouponCode = (index) => {
  if (!coupon.value.couponCodes) return;

  coupon.value.couponCodes.splice(index, 1);
};

const setCost = (items) => {
  var cost = 0

  items.forEach(item => {
    if (item.cost) {
      cost += item.cost
    }
  })

  coupon.value.giftCost = cost / 100.0
}

const loadCoupon = async () => {
  if (!couponId.value) {
    coupon.value = {
      couponCodes: [],
      supportedPlanIds: [],
      supportedPlans: [],
      giftProducts: [],
      giftDescription: "",
      giftCost: undefined,
      availableRedeems: 0,
      lowAvailableRedeemsThreshold: undefined,
      minimumAgeToRedeem: undefined,
      active: true,
    }

    loading.value = false;
    nextTick(() => dirty.value = false)

    return;
  }

  loading.value = true;

  try {
    const c = (await SubscriptionGiftCouponService.find(couponId.value))

    if (c.supportedPlans == null || c.supportedPlans == undefined) {
      c.supportedPlans = []
    }

    c.giftCost = c.giftCost / 100.0

    coupon.value = c;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
    nextTick(() => dirty.value = false)
  }
};

const command = (cmd) => {
  switch (cmd) {
    case "updateAvailableRedeems":
      showUpdateAvailableRedeems.value = true;
      break;
    case "saveAndNew":
      save(true);
      break;
  }
}

const save = async (createOther = false) => {
  try {
    await form.value.validate();
  } catch (e) {
    message.warning("Verifique os dados");
    console.error(e);
    return;
  }

  saving.value = true;

  try {
    const request = {
      couponCodes: coupon.value.couponCodes,
      supportedPlanIds: coupon.value.supportedPlanIds,
      supportedPlans: coupon.value.supportedPlans,
      giftProducts: coupon.value.giftProducts,
      giftDescription: coupon.value.giftDescription,
      giftCost: coupon.value.giftCost * 100.0,
      availableRedeems: coupon.value.availableRedeems,
      lowAvailableRedeemsThreshold:
        coupon.value.lowAvailableRedeemsThreshold == 0 || coupon.value.lowAvailableRedeemsThreshold == undefined ?
          null : coupon.value.lowAvailableRedeemsThreshold,
      minimumAgeToRedeem:
        coupon.value.minimumAgeToRedeem == 0 || coupon.value.lowAvailableRedeemsThreshold == undefined ?
          null : coupon.value.minimumAgeToRedeem ?? null,
      active: coupon.value.active,
    };

    if (isNew.value) {
      const response = await SubscriptionGiftCouponService.create(request);

      dirty.value = false;

      if (createOther) {
        router.replace({ name: "subscription-gift-coupon-new" });
      } else {
        router.replace({
          name: "subscription-gift-coupon-detail",
          params: { id: response.id },
        });
      }
    } else {
      await SubscriptionGiftCouponService.update(coupon.value.id, request);

      dirty.value = false;

      if (createOther) {
        router.replace({ name: "subscription-gift-coupon-new" });
      } else {
        loadCoupon();
      }
    }

    message.success(`Cupom salvo com sucesso`);
  } catch (e) {
    message.error(`Falha ao salvar: ${e}`);
  } finally {
    saving.value = false;
  }
};

const back = () => {
  router.back();
};

watch(() => route.params.id, loadCoupon);
watch(() => coupon.value, (() => {
  dirty.value = true;
}), { deep: true });

const interrupLeave = (event) => {
  if (dirty.value) {
    event.preventDefault()
    event.returnValue = 'Unsaved changes'

    return "Você tem alterações não salvas. Tem certeza que deseja sair?"
  }
}

var beforeRouteLeaveHandle = null
onMounted(async () => {
  loadCoupon();

  window.addEventListener("beforeunload", interrupLeave)

  beforeRouteLeaveHandle = router.beforeEach(async (to, from, next) => {
    if (dirty.value) {
      const answer = window.confirm(
        'Você tem alterações não salvas. Realmente deseja sair desta página?'
      )

      if (!answer) {
        next(false)
      } else {
        next()
      }
    } else {
      next()
    }
  })
});

onUnmounted(() => {
  if (beforeRouteLeaveHandle) {
    beforeRouteLeaveHandle()
  }

  window.removeEventListener("beforeunload", interrupLeave)
});
</script>
<style scoped lang="sass">
.coupons-container
  display: flex
  gap: 8px
  border: #c1c1c1 dashed 1.5px
  border-radius: 4px
  margin-top: 8px
  padding: 8px
</style>
